<template>
    <div class="journey-comment">
        <div class="comment-header">
            <div class="comment-created-by">
                <div class="position-relative" v-if="!isSeen">
                    <has-unseen-comments-marker class="editable-comment-marker" />
                </div>
                
                {{createdBy}}
            </div>
            <span class="comment-created-date">{{createdDate}}</span>
            <span class="comment-created-time">{{createdTime}}</span>
            <div class="comment-actions">
                <div class="comment-action-buttons" v-if="displayActions">
                    <i class="fa fa-pencil mdb-icon-btn edit" @click="displayEditor = true" />
                </div>
                <loader v-else-if="isCommentPublishing" />
            </div>
            
            <span class="comment-sub-type" v-if="subType">{{subType}}</span>
        </div>
        
        <div class="comment-body">
            <template v-if="displayEditor">
                <comment-editor
                    class="comment-editor"
                    v-model="updatedContent"
                />
                <div class="comment-editor-controls">
                    <button class="btn btn-sm btn-outline-secondary" @click="cancelEdit">{{resource('Button_Cancel')}}</button>
                    <button class="btn btn-sm btn-primary" @click="updateComment">{{resource('Button_Save')}}</button>
                </div>
            </template>
            <span v-else>
                {{content}}
            </span>
        </div>
    </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import moment from 'moment';
import {bootstrapDangerGrowl} from "@core/Util/growler";

import resourceMixin from "@core/Util/Mixins/resources";

import CommentEditor from "./CommentEditor";
import HasUnseenCommentsMarker from "./HasUnseenCommentsMarker";
import Loader from "@modules/JourneyBuilder/Components/Elements/Loader";

const {mapGetters, mapActions} = createNamespacedHelpers('journeyBuilder/composer');

export default {
    name: "JourneyComment",
    mixins: [resourceMixin],
    components: {
        CommentEditor,
        HasUnseenCommentsMarker,
        Loader
    },
    methods: {
        ...mapActions({
            async updateComment(dispatch){
                this.displayEditor = false;
                
                //We want to retain our updatedContent on failure
                //if the user desires so, the update can be attempted again without losing the new content
                try {
                    await dispatch('updateComment', {
                        commentId: this.id,
                        content: this.updatedContent,
                        relatedId: this.relatedId
                    });
                } catch (e) {
                    bootstrapDangerGrowl(e);
                }
            }
        }),
        cancelEdit(){
            this.updatedContent = this.content;
            this.displayEditor = false;
        }
    },
    computed: {
        ...mapGetters({
            'isCommentPublishingStore': 'isCommentPublishing'
        }),
        isCommentPublishing(){
            return this.isCommentPublishingStore(this.id);
        },
        createdDate(){
            return moment.utc(this.created)
                .local()
                .format('DD-MM-YYYY');
        },
        createdTime(){
            return moment.utc(this.created)
                .local()
                .format('HH:mm');
        },
        displayActions(){
            //Our actions should be displayed when:
            // * Our comment is marked as editable
            // * Our editor currently isn't shown
            // * Our content currently isn't updating, our update may take a while; we want to avoid race conditions
            return this.isEditable && 
                !this.displayEditor &&
                !this.isCommentPublishing;
        }
    },
    watch: {
        displayEditor(value){
            if(value){
                this.$emit('editor-opened');
            }
        }
    },
    props: {
        id: {
            type: String,
            required: true
        },
        createdBy: {
            type: String,
            required: true
        },
        created: {
            type: String,
            required: true
        },
        content: {
            type: String,
            required: true
        },
        isEditable: {
            type: Boolean,
            required: true
        },
        isSeen: {
            type: Boolean,
            required: true
        },
        relatedId: {
            type: String,
            required: true
        },
        subType: {
            type: String,
            required: false
        }
    },
    data(){
        return {
            displayEditor: false,
            updatedContent: this.content
        }
    }
}
</script>

<style scoped lang="scss">
    .journey-comment {
        text-align: left;
    }

    .comment-header {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        column-gap: 5px;
        padding-right: 5px;
        padding-bottom: 5px;
        
        font-size: 0.9rem;

        .comment-created-by { width: 30%; }
        .comment-created-date { width: 30%; }
        .comment-created-time { width: 20%; }
        .comment-actions { 
            width: calc(20% - 15px);
            
            .comment-action-buttons {
                text-align: right;

                :not(:first-child) {
                    margin-left: 5px;
                }   
            }
        }
        
        .comment-sub-type {
            width: 100%;
        }
    }
    
    .comment-body {
        background-color: #FFFFFF;
        
        padding-left: 5px;
        padding-right: 5px;
    }
    
    .comment-editor {
        padding-top: 5px;
        padding-bottom: 5px;
    }
    
    .comment-editor-controls {
        padding-bottom: 5px;
        text-align: right;
    }
    
    .editable-comment-marker {
        position: absolute;
        left: -10px;
        top: 7px;
    }
</style>